import type { Map as MapboxMap, MapMouseEvent, Popup } from "mapbox-gl";

/**
 * Properties attached to a tour stop feature coming from Mapbox.
 */
export interface StopProperties {
  id?: string | number;
  order?: string | number;
  itinerary?: string | number;
  name?: string;
  text?: string;
  transport?: string;
  date?: string | null;
  waypoint?: boolean | string;
}

/**
 * Normalized Point feature used internally by the tour map.
 * Ensures fixed Point geometry with [lng, lat] tuple.
 */
export type StopFeature = {
  geometry: {
    type: "Point";
    coordinates: [number, number];
  };
  properties: StopProperties;
};

/**
 * Safely extracts Point coordinates from a GeoJSON geometry.
 * Assumes the geometry is a Point at runtime.
 */
export function getPointCoordinates(geometry: unknown): [number, number] {
  return (geometry as { coordinates: [number, number] }).coordinates;
}

/**
 * Wraps longitude to the closest copy of the world relative to cursor longitude.
 * Prevents popup jumping on world-wrapped maps.
 */
export function wrapLngLat(
  coords: [number, number],
  lng: number
): [number, number] {
  const wrapped: [number, number] = [...coords];
  while (Math.abs(lng - wrapped[0]) > 180) {
    wrapped[0] += lng > wrapped[0] ? 360 : -360;
  }
  return wrapped;
}

/**
 * Builds HTML markup for a stop popup.
 * Styling is intentionally inline to avoid CSS dependency.
 */
export function buildPopupHTML(props: StopProperties): string {
  return `
    <div style="
      background:#0a4160;
      padding:14px 16px;
      border-radius:10px;
      color:white;
      font-size:15px;
      line-height:1.45;
      box-shadow:0 4px 14px rgba(0,0,0,0.25);
    ">
      <div style="font-weight:600; font-size:16px; margin-bottom:4px;">
        ${props.name ?? ""}
      </div>
      <div style="font-size:14px; opacity:0.85;">
        ${props.text ?? ""}
      </div>
    </div>
  `;
}

/**
 * Recalculates popup vertical offset after DOM render
 * so the popup sits fully above the point.
 */
export function updatePopupOffset(popup: Popup) {
  requestAnimationFrame(() => {
    const el = document.querySelector(
      ".tourmap-popup .mapboxgl-popup-content > div"
    );
    if (!el) return;
    const h = (el as HTMLElement).offsetHeight || 0;
    popup.setOffset([0, -(h + 20)]);
  });
}

/**
 * Picks the closest stop feature to the mouse event.
 * Used to disambiguate overlapping hit areas.
 */
export function pickNearestStop(
  evt: MapMouseEvent,
  map: MapboxMap
): StopFeature | null {
  const baseFeatures =
    (evt.features && evt.features.length && evt.features) ||
    map.queryRenderedFeatures(evt.point, { layers: ["stops-hit"] });

  if (!baseFeatures?.length) return null;

  let best = baseFeatures[0];
  let bestDist = Infinity;

  for (const f of baseFeatures) {
    const [lng, lat] = getPointCoordinates(f.geometry);
    const dx = lng - evt.lngLat.lng;
    const dy = lat - evt.lngLat.lat;
    const d2 = dx * dx + dy * dy;

    if (d2 < bestDist) {
      bestDist = d2;
      best = f;
    }
  }

  return {
    geometry: {
      type: "Point",
      coordinates: getPointCoordinates(best.geometry),
    },
    properties: best.properties as StopProperties,
  };
}
